import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { FileUser, Download, Loader2, User, Calendar, Check, ChevronsUpDown, Printer, FileSpreadsheet, Send, ArrowLeft, Eye } from "lucide-react";
import { cn } from "../../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Helper to strip HTML tags
const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};

const DATE_RANGES = [
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "ytd", label: "Year to Date (YTD)" },
];

const REPORT_FORMATS = [
  { value: "column", label: "Column Format" },
  { value: "row", label: "Row Format" },
];

export default function IndividualUserReportPage() {
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRange, setSelectedRange] = useState("this_week");
  const [reportFormat, setReportFormat] = useState("column");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  
  // Report display state
  const [reportData, setReportData] = useState(null);
  const [showReport, setShowReport] = useState(false);
  
  // Forward dialog state
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [forwardEmail, setForwardEmail] = useState("");
  const [forwardMessage, setForwardMessage] = useState("");
  const [ccTeamLeader, setCcTeamLeader] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  
  const reportRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/teams`)
      ]);
      const activeUsers = usersRes.data.filter(u => u.is_active && !u.is_exempt);
      // Sort alphabetically by full_name
      activeUsers.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
      setUsers(activeUsers);
      setTeams(teamsRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const getTeamNames = (user) => {
    if (!user?.team_ids || user.team_ids.length === 0) return "No Team";
    return user.team_ids
      .map(tid => teams.find(t => t.id === tid)?.name)
      .filter(Boolean)
      .join(", ") || "No Team";
  };

  const selectedUser = users.find(u => u.id === selectedUserId);

  const handleGenerateReport = async () => {
    if (!selectedUserId) {
      toast.error("Please select a user");
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/admin/individual-user-report/data`, {
        user_id: selectedUserId,
        date_range: selectedRange
      });
      
      setReportData(response.data);
      setShowReport(true);
      toast.success("Report generated!");
    } catch (err) {
      console.error("Error generating report:", err);
      toast.error(err.response?.data?.detail || "Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/individual-user-report`,
        { user_id: selectedUserId, date_range: selectedRange },
        { responseType: 'blob', timeout: 60000 }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportData?.user?.name?.replace(/\s+/g, '_') || 'user'}_${selectedRange}_report.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch (err) {
      toast.error("Failed to download PDF");
    }
  };

  const handleExportCSV = () => {
    if (!reportData) return;
    
    // Build CSV content - dynamic headers based on form fields
    const headers = ["Date", "Timing"];
    reportData.form_fields?.forEach(field => headers.push(field.label));
    
    const rows = reportData.rows.map(row => {
      const csvRow = [row.date, row.timing];
      reportData.form_fields?.forEach(field => {
        const value = row[field.id] || "-";
        const cleanValue = stripHtml(value).replace(/"/g, '""');
        csvRow.push(`"${cleanValue}"`);
      });
      return csvRow;
    });
    
    const csvContent = [
      `User Report: ${reportData.user.name}`,
      `Team: ${reportData.team_name}`,
      `Team Leader: ${reportData.team_leader_name}`,
      `Period: ${reportData.date_range.label} (${reportData.date_range.start} - ${reportData.date_range.end})`,
      `Generated: ${reportData.generated_at}`,
      "",
      headers.join(","),
      ...rows.map(r => r.join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportData.user.name.replace(/\s+/g, '_')}_${selectedRange}_report.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported!");
  };

  const handlePrint = () => {
    const printContent = reportRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>User Report - ${reportData?.user?.name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { margin-bottom: 20px; }
          .header h1 { color: #1e40af; margin: 0; font-size: 24px; }
          .header p { color: #64748b; margin: 4px 0; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #1e40af; color: white; padding: 10px; text-align: left; font-size: 12px; }
          td { border: 1px solid #e2e8f0; padding: 8px; font-size: 11px; vertical-align: top; }
          tr:nth-child(even) { background: #f8fafc; }
          .timing { text-align: center; font-weight: bold; }
          .timing-b { color: #16a34a; }
          .timing-a { color: #dc2626; }
          .no-log { color: #94a3b8; font-style: italic; }
          .footer { margin-top: 20px; font-size: 11px; color: #64748b; border-top: 1px solid #e2e8f0; padding-top: 10px; }
          @media print {
            body { padding: 0; }
            @page { margin: 0.5in; size: landscape; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>User Report: ${reportData?.user?.name}</h1>
          <p>Team: ${reportData?.team_name} | Team Leader: ${reportData?.team_leader_name}</p>
          <p>Period: ${reportData?.date_range?.label} (${reportData?.date_range?.start} - ${reportData?.date_range?.end})</p>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 100px;">Date</th>
              <th style="width: 60px;">Timing</th>
              <th>Tasks Worked On Today</th>
              <th>Challenges and Hurdles</th>
              <th>Plan for Tomorrow</th>
            </tr>
          </thead>
          <tbody>
            ${reportData?.rows?.map(row => `
              <tr>
                <td>${row.date}</td>
                <td class="timing ${row.timing === 'B' ? 'timing-b' : row.timing === 'A' ? 'timing-a' : ''}">${row.timing}</td>
                <td class="${!row.has_log && !row.day_type ? 'no-log' : ''}">${row.tasks_today || '-'}</td>
                <td class="${!row.has_log ? 'no-log' : ''}">${row.challenges || '-'}</td>
                <td class="${!row.has_log ? 'no-log' : ''}">${row.plan_tomorrow || '-'}</td>
              </tr>
            `).join('') || ''}
          </tbody>
        </table>
        <div class="footer">
          <p>Generated: ${reportData?.generated_at} | B = Before Deadline, A = After Deadline</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  const handleForward = async () => {
    if (!forwardEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    
    setForwarding(true);
    try {
      const response = await axios.post(`${API}/admin/individual-user-report/forward`, {
        user_id: selectedUserId,
        date_range: selectedRange,
        email: forwardEmail.trim(),
        message: forwardMessage.trim(),
        cc_team_leader: ccTeamLeader
      });
      
      toast.success(response.data.message);
      setForwardDialogOpen(false);
      setForwardEmail("");
      setForwardMessage("");
      setCcTeamLeader(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to forward report");
    } finally {
      setForwarding(false);
    }
  };

  const handleBack = () => {
    setShowReport(false);
    setReportData(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Report View
  if (showReport && reportData) {
    return (
      <div className="space-y-6">
        {/* Header with actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">User Report: {reportData.user.name}</h1>
              <p className="text-slate-500 text-sm">
                {reportData.team_name} | {reportData.date_range.label} ({reportData.date_range.start} - {reportData.date_range.end})
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-btn">
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} data-testid="download-pdf-btn">
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-csv-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              CSV
            </Button>
            <Button size="sm" onClick={() => setForwardDialogOpen(true)} data-testid="forward-btn">
              <Send className="h-4 w-4 mr-2" />
              Forward
            </Button>
          </div>
        </div>

        {/* Report Card */}
        <Card ref={reportRef}>
          <CardHeader className="bg-slate-50 border-b">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-slate-500">User</span>
                <p className="font-semibold">{reportData.user.name}</p>
                <p className="text-xs text-slate-400">{reportData.user.email}</p>
              </div>
              <div>
                <span className="text-slate-500">Team</span>
                <p className="font-semibold">{reportData.team_name}</p>
              </div>
              <div>
                <span className="text-slate-500">Team Leader</span>
                <p className="font-semibold">{reportData.team_leader_name}</p>
              </div>
              <div>
                <span className="text-slate-500">Period</span>
                <p className="font-semibold">{reportData.date_range.label}</p>
                <p className="text-xs text-slate-400">{reportData.date_range.start} - {reportData.date_range.end}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              {reportFormat === "column" ? (
                /* Column Format - Original horizontal layout */
                <table className="w-full">
                  <thead>
                    <tr className="bg-blue-800 text-white">
                      <th className="px-4 py-3 text-left text-xs font-semibold w-28">Date</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold w-16">Timing</th>
                      {reportData.form_fields?.map((field) => (
                        <th key={field.id} className="px-4 py-3 text-left text-xs font-semibold">
                          {field.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.rows.map((row, idx) => (
                      <tr key={row.date_raw} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                        <td className="px-4 py-3 text-sm font-medium text-slate-700 whitespace-nowrap">{row.date}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={cn(
                            "font-bold text-sm",
                            row.timing === "B" && "text-green-600",
                            row.timing === "A" && "text-red-600",
                            row.timing === "-" && "text-slate-400"
                          )}>
                            {row.timing}
                          </span>
                        </td>
                        {reportData.form_fields?.map((field) => (
                          <td 
                            key={field.id}
                            className={cn(
                              "px-4 py-3 text-sm prose prose-sm max-w-none [&_p]:my-0",
                              !row.has_log && !row.day_type && "text-slate-400 italic"
                            )}
                          >
                            {row[field.id] ? (
                              <div dangerouslySetInnerHTML={{ __html: row[field.id] }} />
                            ) : "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                /* Row Format - Vertical layout with labels above content */
                <table className="w-full">
                  <thead>
                    <tr className="bg-blue-800 text-white">
                      <th className="px-4 py-3 text-left text-xs font-semibold w-36">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold">Log Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.rows.map((row, idx) => {
                      // Build fields array dynamically from form_fields
                      const fields = reportData.form_fields?.map(field => ({
                        label: field.label,
                        content: row[field.id]
                      })) || [];
                      
                      // Filter out truly empty fields (keep numbers including 0)
                      const nonEmptyFields = fields.filter(f => {
                        if (f.content === null || f.content === undefined) return false;
                        if (typeof f.content === 'number') return true; // Keep all numbers including 0
                        if (typeof f.content === 'string') return f.content.trim() !== '';
                        return Boolean(f.content);
                      });
                      
                      // Check if this is a day type entry (sick, vacation, etc.)
                      const isDayType = row.day_type;
                      
                      return (
                        <tr 
                          key={row.date_raw} 
                          className={cn(
                            idx % 2 === 0 ? "bg-white" : "bg-slate-50",
                            "border-t border-slate-200"
                          )}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-slate-700 whitespace-nowrap align-top">
                            <div className="font-semibold">{row.date}</div>
                            <span className={cn(
                              "text-xs",
                              row.timing === "B" && "text-green-600",
                              row.timing === "A" && "text-red-600",
                              row.timing === "-" && "text-slate-400"
                            )}>
                              {row.timing === "B" ? "Before Deadline" : row.timing === "A" ? "After Deadline" : row.timing === "-" ? "Not Submitted" : ""}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {isDayType ? (
                              <div className="italic text-amber-700">
                                {row[reportData.form_fields?.[0]?.id] ? (
                                  <div dangerouslySetInnerHTML={{ __html: row[reportData.form_fields[0].id] }} />
                                ) : `[${row.day_type.toUpperCase().replace('_', ' ')}]`}
                              </div>
                            ) : nonEmptyFields.length > 0 ? (
                              <div className="space-y-4">
                                {nonEmptyFields.map((field, fieldIdx) => (
                                  <div key={fieldIdx}>
                                    <div className="font-semibold text-blue-700 mb-1">{field.label}</div>
                                    <div className="prose prose-sm max-w-none [&_p]:my-0" dangerouslySetInnerHTML={{ __html: field.content }} />
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-slate-400 italic">No log submitted</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </CardContent>
          <div className="px-4 py-3 bg-slate-50 border-t text-xs text-slate-500 flex justify-between">
            <span>Generated: {reportData.generated_at}</span>
            <span>B = Before Deadline, A = After Deadline</span>
          </div>
        </Card>

        {/* Forward Dialog */}
        <Dialog open={forwardDialogOpen} onOpenChange={setForwardDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-primary" />
                Forward Report
              </DialogTitle>
              <DialogDescription>
                Send this report to an email address
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  type="email"
                  placeholder="recipient@example.com"
                  value={forwardEmail}
                  onChange={(e) => setForwardEmail(e.target.value)}
                  data-testid="forward-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Message <span className="text-xs text-slate-400">(Optional)</span></Label>
                <textarea
                  className="w-full min-h-[100px] p-3 text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Enter a message to include in the email..."
                  value={forwardMessage}
                  onChange={(e) => setForwardMessage(e.target.value)}
                  data-testid="forward-message-input"
                />
              </div>
              {reportData.team_leader_emails?.length > 0 && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="cc-leader"
                    checked={ccTeamLeader}
                    onCheckedChange={setCcTeamLeader}
                  />
                  <Label htmlFor="cc-leader" className="text-sm cursor-pointer">
                    CC Team Leader ({reportData.team_leader_emails.join(", ")})
                  </Label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setForwardDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleForward} disabled={forwarding} data-testid="send-forward-btn">
                {forwarding ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Report
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Selection Form
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">User Report</h1>
        <p className="text-slate-500 mt-1">Generate detailed log report for an individual user</p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileUser className="h-5 w-5 text-primary" />
            Generate Individual Report
          </CardTitle>
          <CardDescription>
            Select a user and date range to generate a comprehensive report of their daily logs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* User Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Select User
            </Label>
            <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={userSearchOpen}
                  className="w-full justify-between h-auto min-h-[44px] py-2"
                  data-testid="user-select-trigger"
                >
                  {selectedUser ? (
                    <div className="flex flex-col items-start">
                      <span className="font-medium">{selectedUser.full_name}</span>
                      <span className="text-xs text-slate-500">{selectedUser.email} • {getTeamNames(selectedUser)}</span>
                    </div>
                  ) : (
                    <span className="text-slate-500">Search and select a user...</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search users by name or email..." />
                  <CommandList>
                    <CommandEmpty>No user found.</CommandEmpty>
                    <CommandGroup>
                      {users.map((user) => (
                        <CommandItem
                          key={user.id}
                          value={`${user.full_name} ${user.email}`}
                          onSelect={() => {
                            setSelectedUserId(user.id);
                            setUserSearchOpen(false);
                          }}
                          className="flex items-center justify-between py-3"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{user.full_name}</span>
                            <span className="text-xs text-slate-500">{user.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">{getTeamNames(user)}</span>
                            <Check
                              className={cn(
                                "h-4 w-4",
                                selectedUserId === user.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Date Range Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Date Range
            </Label>
            <Select value={selectedRange} onValueChange={setSelectedRange}>
              <SelectTrigger className="w-full" data-testid="date-range-select">
                <SelectValue placeholder="Select date range" />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Report Format Selection */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileUser className="h-4 w-4" />
              Report Format
            </Label>
            <Select value={reportFormat} onValueChange={setReportFormat}>
              <SelectTrigger className="w-full" data-testid="report-format-select">
                <SelectValue placeholder="Select report format" />
              </SelectTrigger>
              <SelectContent>
                {REPORT_FORMATS.map((format) => (
                  <SelectItem key={format.value} value={format.value}>
                    {format.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              {reportFormat === "column" 
                ? "Shows each field in a separate column" 
                : "Shows each date with fields stacked as rows"}
            </p>
          </div>

          {/* Selected Summary */}
          {selectedUser && (
            <div className="bg-slate-50 rounded-lg p-4 border">
              <h4 className="font-medium text-slate-900 mb-2">Report Preview</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">User:</span>
                  <p className="font-medium">{selectedUser.full_name}</p>
                </div>
                <div>
                  <span className="text-slate-500">Team:</span>
                  <p className="font-medium">{getTeamNames(selectedUser)}</p>
                </div>
                <div>
                  <span className="text-slate-500">Email:</span>
                  <p className="font-medium">{selectedUser.email}</p>
                </div>
                <div>
                  <span className="text-slate-500">Date Range:</span>
                  <p className="font-medium">{DATE_RANGES.find(r => r.value === selectedRange)?.label}</p>
                </div>
              </div>
            </div>
          )}

          {/* Generate Button */}
          <Button
            onClick={handleGenerateReport}
            disabled={!selectedUserId || generating}
            className="w-full"
            size="lg"
            data-testid="generate-report-btn"
          >
            {generating ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Generating Report...
              </>
            ) : (
              <>
                <Eye className="mr-2 h-5 w-5" />
                View Report
              </>
            )}
          </Button>

          <p className="text-xs text-slate-500 text-center">
            Report will be generated based on the form assigned to the user. 
            Includes Date, Submission timing (B=Before deadline, A=After deadline), and all form fields.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
